import React from 'react';

interface StatusItem {
    status: string;
    // Support both 'count' and 'value' field names from API
    count?: number;
    value?: number;
}

interface StatusSummaryProps {
    data: StatusItem[] | { data: StatusItem[]; metadata?: any };
    options?: {
        show_counts?: boolean;
        show_labels?: boolean;
        status_colors?: Record<string, string>;
        status_labels?: Record<string, string>;
    };
}

// Map numeric status codes to human-readable labels
// Common review cycle status codes
const STATUS_CODE_LABELS: Record<string, string> = {
    '0': 'Not Started',
    '1': 'In Progress',
    '2': 'Completed',
    '3': 'Pending Review',
    '4': 'Approved',
    '5': 'Rejected',
    '6': 'On Hold',
    '7': 'Cancelled',
    // Goal status codes
    '10': 'Draft',
    '20': 'Active',
    '30': 'Completed',
    '40': 'Archived',
};

const formatStatus = (status: string, customLabels?: Record<string, string>) => {
    // Check custom labels first
    if (customLabels && customLabels[status]) {
        return customLabels[status];
    }
    // Check numeric status code mappings
    if (STATUS_CODE_LABELS[status]) {
        return STATUS_CODE_LABELS[status];
    }
    // Fallback: format the string (replace underscores, capitalize)
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const StatusSummary: React.FC<StatusSummaryProps> = ({ data, options = {} }) => {
    const { show_counts = true, show_labels = true, status_colors = {}, status_labels = {} } = options;

    // Handle case where data might be wrapped in { data: [...] }
    const items: StatusItem[] = Array.isArray(data) ? data : (data && 'data' in data) ? data.data : [];

    const defaultColors: Record<string, string> = {
        // Text-based statuses
        completed: '#10b981',
        in_progress: '#3b82f6',
        'in progress': '#3b82f6',
        pending: '#f59e0b',
        'pending review': '#f59e0b',
        not_started: '#9ca3af',
        'not started': '#9ca3af',
        draft: '#9ca3af',
        overdue: '#ef4444',
        rejected: '#ef4444',
        approved: '#10b981',
        active: '#3b82f6',
        'on hold': '#f59e0b',
        cancelled: '#6b7280',
        archived: '#6b7280',
        // Numeric status codes
        '0': '#9ca3af',  // Not Started
        '1': '#3b82f6',  // In Progress
        '2': '#10b981',  // Completed
        '3': '#f59e0b',  // Pending Review
        '4': '#10b981',  // Approved
        '5': '#ef4444',  // Rejected
        '6': '#f59e0b',  // On Hold
        '7': '#6b7280',  // Cancelled
        '10': '#9ca3af', // Draft
        '20': '#3b82f6', // Active
        '30': '#10b981', // Completed
        '40': '#6b7280', // Archived
        ...status_colors
    };

    if (!items || items.length === 0) {
        return <div className="flex items-center justify-center h-full text-gray-400 text-sm">No data available</div>;
    }

    // Get color for a status (check raw status first, then mapped label)
    const getStatusColor = (status: string): string => {
        // Try exact match first
        if (defaultColors[status]) return defaultColors[status];
        // Try lowercase
        const lowerStatus = status.toLowerCase();
        if (defaultColors[lowerStatus]) return defaultColors[lowerStatus];
        // Try the mapped label
        const mappedLabel = formatStatus(status, status_labels).toLowerCase();
        if (defaultColors[mappedLabel]) return defaultColors[mappedLabel];
        // Default gray
        return '#6b7280';
    };

    return (
        <div className="grid grid-cols-2 gap-4 p-4 h-full">
            {items.map((item, index) => {
                const color = getStatusColor(item.status || '');
                // Support both 'count' and 'value' field names
                const itemCount = item.count ?? item.value ?? 0;

                return (
                    <div
                        key={`${item.status}-${index}`}
                        className="flex flex-col items-center justify-center p-3 rounded-lg border border-gray-100 bg-white shadow-sm"
                        style={{ borderTop: `3px solid ${color}` }}
                    >
                        {show_counts && (
                            <span className="text-2xl font-bold text-gray-900 mb-1">
                                {itemCount}
                            </span>
                        )}
                        {show_labels && (
                            <span className="text-xs font-medium text-gray-500 text-center">
                                {formatStatus(item.status, status_labels)}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
